/**
 * useRevenueHistory — Fetches fee revenue history for a creator's token.
 *
 * Requires wallet connection and access token (creator endpoint).
 * Returns time-series of claimed + cumulative fee revenue.
 *
 * Story 8.4 — Task 1
 */

import type { PriceInterval } from "@repo/shared-types";
import { useQuery } from "@tanstack/react-query";
import { creatorApi } from "../../lib/api";
import { useWallet } from "../use-wallet";
import { creatorKeys } from "./query-keys";

const STALE_TIMES: Record<PriceInterval, number> = {
	"1H": 30_000,
	"4H": 30_000,
	"1D": 60_000,
	"1W": 60_000,
};

export function useRevenueHistory(tokenId: string, interval: PriceInterval) {
	const { address, getAccessToken } = useWallet();

	return useQuery({
		queryKey: creatorKeys.revenueHistory(tokenId, interval, address),
		queryFn: async () => {
			const accessToken = await getAccessToken();
			if (!accessToken) {
				// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors
				throw new Error("No access token available");
			}
			const response = await creatorApi.getRevenueHistory(tokenId, interval, accessToken, address);
			return response.data;
		},
		enabled: !!address && !!tokenId,
		staleTime: STALE_TIMES[interval],
	});
}
